/**
 * Where a ship picker and a date picker read and write - Explore's selection,
 * or something else's.
 *
 * Until now there was only one answer: the `selectedShip` / `comparisonShips` /
 * `startDate` / `endDate` / `activePreset` atoms, which every fetch path reads.
 * A dashboard that owns its ship and range needs a second answer, and the two
 * must not be the same store: a dashboard detached from Explore is detached,
 * and moving its ship must not move the one Explore is plotting - nor the other
 * way round.
 *
 * The pickers therefore stop touching the atoms directly and go through a
 * scope. `exploreSelection` is the atoms, with every side effect they have
 * always had (loading the ship's columns, the estimate, the URL). A dashboard
 * passes its own, in `dashboard-selection.ts`, which writes the document
 * instead and marks it unsaved; the Model Viewer passes `model-selection.ts`,
 * which writes the ships and window its models share.
 *
 * The operations are what the pickers actually do, not setters: "remove this
 * ship" means promote the next one to anchor and reload columns in Explore, and
 * means nothing of the sort in a dashboard. Keeping the verb whole is what lets
 * the two answers differ where they genuinely differ.
 */

import {
  activeTab,
  comparisonShips,
  plottedShipIds,
  selectedShip,
  startDate,
  endDate,
  activePreset,
  MAX_PLOT_SHIPS,
  type DatePreset,
} from '../../state/store.ts';
import { periodComparison } from '../../state/period-comparison.ts';
import { plotFocusShip } from '../plot/plot-ui-state.ts';
import { syncToUrl } from '../../state/url-sync.ts';
import { applyDatePreset, setPresetActive, shiftDateWindow } from './date-presets.ts';
import { scheduleEstimate } from '../../state/estimate.ts';
import { loadColumns } from './ship-data.ts';
import { ensureShipColumns } from './ship-columns.ts';
import type { WindowShift } from '../../utils/dates.ts';

export interface SelectionScope {
  /** For debugging and for the few places that legitimately care. */
  readonly kind: 'explore' | 'dashboard' | 'model' | 'live';

  /* ── ships ─────────────────────────────────────────────── */
  ships(): string[];
  anchor(): string | null;
  /** Whether more than one ship may be picked here. */
  multiAllowed(): boolean;
  /** Replace the selection with this one ship. */
  pickOnly(id: string): Promise<void>;
  /** Add a comparison ship to the ones already picked. */
  addShip(id: string): Promise<void>;
  /** Take one off; removing the anchor promotes the next. */
  removeShip(id: string): Promise<void>;

  /* ── window ────────────────────────────────────────────── */
  start(): string;
  end(): string;
  preset(): DatePreset;
  applyPreset(preset: DatePreset): void;
  setStart(value: string): void;
  setEnd(value: string): void;
  shift(shift: WindowShift): void;

  /**
   * Called when the values behind this scope change somewhere else, so a picker
   * that is not watching the global atoms still re-renders. The atoms already
   * notify their own subscribers, so Explore's scope has nothing to add.
   */
  subscribe(listener: () => void): () => void;
}

/** Explore's selection: the atoms, and everything that has always followed them. */
export const exploreSelection: SelectionScope = {
  kind: 'explore',

  ships: () => plottedShipIds(),
  anchor: () => selectedShip.value,

  // Comparison ships are a Plot-tab idea only; Export is single-ship. Every
  // data source can draw several ships.
  multiAllowed: () => activeTab.value === 'plot' && !periodComparison.value,

  async pickOnly(id: string): Promise<void> {
    comparisonShips.set([]);
    await loadColumns(id);
    syncToUrl();
  },

  async addShip(id: string): Promise<void> {
    if (!selectedShip.value) {
      await loadColumns(id);
      syncToUrl();
      return;
    }
    if (plottedShipIds().length >= MAX_PLOT_SHIPS) return;
    comparisonShips.set([...comparisonShips.value, id]);
    // Awaited so the Y picker's coverage badges are right as the chip appears.
    await ensureShipColumns(plottedShipIds());
    syncToUrl();
  },

  async removeShip(id: string): Promise<void> {
    if (plotFocusShip.value === id) plotFocusShip.set(null);
    if (id === selectedShip.value) {
      // Promote the next ship instead of emptying the selection - the anchor is
      // load-bearing for columns, dates and Export.
      const [next, ...rest] = comparisonShips.value;
      comparisonShips.set(rest);
      await loadColumns(next ?? null);
    } else {
      comparisonShips.set(comparisonShips.value.filter((s) => s !== id));
    }
    syncToUrl();
  },

  start: () => startDate.value,
  end: () => endDate.value,
  preset: () => activePreset.value,

  applyPreset: (preset: DatePreset) => applyDatePreset(preset),

  setStart(value: string): void {
    startDate.set(value);
    setPresetActive('custom');
    scheduleEstimate();
    syncToUrl();
  },

  setEnd(value: string): void {
    endDate.set(value);
    setPresetActive('custom');
    scheduleEstimate();
    syncToUrl();
  },

  shift(shift: WindowShift): void {
    shiftDateWindow(shift);
  },

  subscribe: () => () => {},
};
